"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";

/**
 * Tab-scoped navigation stack written to `sessionStorage`. Mounted once in
 * the root layout: pushes the current pathname on every route change.
 *
 * Components that need to know "did the user navigate within our app before
 * landing here?" read `pc:nav-stack`. This is a real signal — unlike
 * `document.referrer`, the browser can't leak it across sessions.
 *
 * Stack is capped to avoid unbounded growth in long-lived tabs.
 */
const NAV_STACK_KEY = "pc:nav-stack";
const MAX_STACK = 20;

export function NavTracker(): null {
	const pathname = usePathname();

	useEffect(() => {
		try {
			const raw = sessionStorage.getItem(NAV_STACK_KEY);
			const stack: string[] = raw ? JSON.parse(raw) : [];
			if (stack[stack.length - 1] === pathname) return;
			const next = [...stack, pathname].slice(-MAX_STACK);
			sessionStorage.setItem(NAV_STACK_KEY, JSON.stringify(next));
		} catch {
			/* sessionStorage unavailable (privacy mode, etc) — no-op */
		}
	}, [pathname]);

	return null;
}

// `history.length <= 1` guards Cmd+click-into-new-tab: Chrome clones the
// source tab's sessionStorage, but the new tab's history is fresh — without
// this check, Back would call `history.back()` and land on about:newtab.
export function readHasPriorInAppNav(currentPathname: string): boolean {
	if (typeof window === "undefined") return false;
	if (window.history.length <= 1) return false;
	try {
		const raw = sessionStorage.getItem(NAV_STACK_KEY);
		if (!raw) return false;
		const stack: string[] = JSON.parse(raw);
		return stack.some((p) => p !== currentPathname);
	} catch {
		return false;
	}
}
